import {
  Bell,
  Bot,
  Gauge,
  LayoutGrid,
  Link2,
  UserRound,
  Users,
  type LucideIcon,
} from "lucide-react";
import type { InstallableModuleKey } from "@playbookai/types";
import type { IdTokenClaims } from "../auth/auth-store.js";
import { canAdministerWorkspace } from "../auth/roles.js";
import { isModuleAvailable } from "../modules/availability.js";
import {
  TEAMS_ROUTE,
  SETTINGS_CONNECTIONS_ROUTE,
  SETTINGS_KPI_ROUTE,
  SETTINGS_ROUTE,
  SETTINGS_SECTION_ROUTES,
} from "../routes.js";

/**
 * The ONE registry of Settings sections (PB-5134, d3/d24). The shared rail,
 * the rail search and the universal search all read it, so the three can never
 * drift apart. Routes are resolved through this module; search data carries
 * ids only ("routes never travel through search data").
 */
export type SettingsGroup = "Personal" | "Collaboration" | "Administration";

export interface SettingsSectionDef {
  readonly id: string;
  readonly label: string;
  readonly description: string;
  readonly keywords: readonly string[];
  readonly icon?: LucideIcon;
  readonly group: SettingsGroup;
  readonly route: string;
  /** Rendered indented under this section in the rail. */
  readonly parentId?: string;
  /** Hidden from members; the server stays the authoritative gate. */
  readonly adminOnly?: boolean;
  /** The module this section belongs to; hidden while it is off (ADR-0100). */
  readonly module?: InstallableModuleKey;
}

export const SETTINGS_SECTIONS: readonly SettingsSectionDef[] = [
  {
    id: "account",
    label: "Account",
    description: "Identity & sign-in",
    keywords: [
      "identity",
      "profile",
      "theme",
      "dark mode",
      "high contrast",
      "time zone",
      "sign-in",
    ],
    icon: UserRound,
    group: "Personal",
    route: SETTINGS_ROUTE,
  },
  {
    id: "player-ai",
    label: "Player AI",
    description: "Assistant behavior",
    keywords: ["assistant", "tone", "verbosity", "connected apps", "tools"],
    icon: Bot,
    group: "Personal",
    route:
      SETTINGS_SECTION_ROUTES["player-ai"] ?? `${SETTINGS_ROUTE}/player-ai`,
  },
  {
    id: "notifications",
    label: "Notifications",
    description: "Channels & alerts",
    keywords: ["email", "in-app", "alerts", "messages"],
    icon: Bell,
    group: "Personal",
    route:
      SETTINGS_SECTION_ROUTES["notifications"] ??
      `${SETTINGS_ROUTE}/notifications`,
  },
  {
    id: "launcher",
    label: "Launcher",
    description: "Modules & landing",
    keywords: [
      "modules",
      "add",
      "remove",
      "reorder",
      "launcher",
      "apps",
      "favorites",
      "favourites",
      "landing",
      "home",
    ],
    icon: LayoutGrid,
    group: "Personal",
    route: SETTINGS_SECTION_ROUTES["launcher"] ?? `${SETTINGS_ROUTE}/launcher`,
  },
  /* Personal, deliberately not admin-only (PB-1592): a member who cannot
     configure an integration must still connect their own account to one. */
  {
    id: "connections",
    label: "Your connections",
    description: "Connected accounts",
    keywords: [
      "accounts",
      "connected",
      "sign in",
      "authorize",
      "credentials",
      "mcp",
    ],
    icon: Link2,
    group: "Personal",
    route: SETTINGS_CONNECTIONS_ROUTE,
  },
  {
    id: "teams",
    label: "Teams",
    description: "People you collaborate with",
    keywords: ["collaboration", "members", "invitations", "join", "owners"],
    icon: Users,
    group: "Collaboration",
    route: TEAMS_ROUTE,
  },
  {
    id: "kpi",
    label: "KPI Manager",
    description: "Definitions & targets",
    keywords: ["metrics", "goals", "owners", "targets", "governance"],
    icon: Gauge,
    group: "Administration",
    route: SETTINGS_KPI_ROUTE,
    module: "kpi",
  },
];

const SECTION_BY_ID = new Map(SETTINGS_SECTIONS.map((s) => [s.id, s]));

export function settingsSectionById(
  id: string,
): SettingsSectionDef | undefined {
  return SECTION_BY_ID.get(id);
}

/** Sections this user may see. Hides admin-only entries and the sections of
 * a module that is off (ADR-0100); never a gate. */
export function visibleSettingsSections(
  claims: IdTokenClaims | null,
): readonly SettingsSectionDef[] {
  const admin = canAdministerWorkspace(claims);
  return SETTINGS_SECTIONS.filter(
    (section) =>
      (admin || !section.adminOnly) &&
      (section.module === undefined ||
        isModuleAvailable(section.module, claims?.enabledModules)),
  );
}

/**
 * Rail search: label, description and keywords, `toLowerCase` (never the
 * locale-sensitive form, so "kpi" keeps matching under Turkish casing). A
 * matching child keeps its parent so the indentation still reads.
 */
export function filterSettingsSections(
  sections: readonly SettingsSectionDef[],
  query: string,
): readonly SettingsSectionDef[] {
  const needle = query.trim().toLowerCase();
  if (!needle) return sections;
  const matches = new Set(
    sections
      .filter((section) =>
        [section.label, section.description, ...section.keywords]
          .join(" ")
          .toLowerCase()
          .includes(needle),
      )
      .map((section) => section.id),
  );
  for (const section of sections) {
    if (matches.has(section.id) && section.parentId)
      matches.add(section.parentId);
  }
  return sections.filter((section) => matches.has(section.id));
}

/** Which rail entry the location belongs to. */
export function settingsSectionIdForPath(pathname: string): string {
  const path = pathname.replace(/\/+$/, "");
  if (path === TEAMS_ROUTE || path.startsWith(`${TEAMS_ROUTE}/`))
    return "teams";
  if (path === SETTINGS_ROUTE) return "account";
  if (path === SETTINGS_KPI_ROUTE || path.startsWith(`${SETTINGS_KPI_ROUTE}/`))
    return "kpi";
  const segment = path.split("/")[2] ?? "";
  return SECTION_BY_ID.has(segment) ? segment : "account";
}

/** Universal-search projection: ids only; the select handler maps them back. */
export const SETTINGS_SEARCH_PREFIX = "settings:";

export interface SettingsSearchItem {
  readonly id: string;
  readonly title: string;
  readonly meta: string;
  readonly icon: LucideIcon;
}

export function settingsSearchItems(
  claims: IdTokenClaims | null,
): readonly SettingsSearchItem[] {
  return visibleSettingsSections(claims).map((section) => {
    const parent = section.parentId
      ? SECTION_BY_ID.get(section.parentId)
      : undefined;
    return {
      id: `${SETTINGS_SEARCH_PREFIX}${section.id}`,
      title: parent ? `${parent.label} · ${section.label}` : section.label,
      meta: section.description,
      icon: section.icon ?? parent?.icon ?? UserRound,
    };
  });
}

export function settingsRouteForSearchId(id: string): string | null {
  if (!id.startsWith(SETTINGS_SEARCH_PREFIX)) return null;
  return (
    SECTION_BY_ID.get(id.slice(SETTINGS_SEARCH_PREFIX.length))?.route ?? null
  );
}
